import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '@/constants/routes'
import { FiChevronLeft, FiChevronRight, FiChevronDown, FiLoader, FiX } from 'react-icons/fi'
import RangeCalendar from '@/components/ui/RangeCalendar'
import { useReports } from '@/hooks/useReports'
import { exportReport, getDailyAISummaryLatest, runDailyAISummaryNow, type DailyAISummaryReport } from '@/services/api/reports'
import { getDocumentsByEntity, type DocumentListItem } from '@/services/api/documents'
import apiService from '@/services/api'
import FinancePurchases from '@/pages/finance/FinancePurchases'
import PageSizeSelect from '@/components/common/PageSizeSelect'
type TabKey = 'contracts' | 'shipments' | 'inventory' | 'stock' | 'payments' | 'supplier' | 'client' | 'product'

// ────────────────────────── Helpers ──────────────────────────

const formatDate = (v: string | null | undefined): string => {
  if (!v) return '—'
  const d = new Date(v)
  if (isNaN(d.getTime())) return v
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const formatCurrency = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined) return '—'
  const num = typeof v === 'string' ? parseFloat(v.replace(/[^0-9.-]/g, '')) : v
  if (isNaN(num)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(num)
}

const formatApiDate = (d: Date): string => d.toISOString().split('T')[0]

const getStatusColor = (status: string) => {
  const s = (status || '').toLowerCase()
  if (['paid', 'cleared', 'completed', 'payment_received'].includes(s))
    return { textColor: '#13AB5B', bgColor: '#E8F7EF' }
  if (['pending', 'awaiting_payment', 'scheduled_payment'].includes(s))
    return { textColor: '#EEC233', bgColor: '#FEF9EB' }
  if (['overdue', 'on hold', 'on_hold', 'rerouted', 'delayed', 'critical'].includes(s))
    return { textColor: '#E93323', bgColor: '#FDEBE9' }
  if (['in progress', 'in_progress', 'in transit', 'in_transit', 'on track', 'partial', 'partially_paid', 'partially_collected'].includes(s))
    return { textColor: '#2C71F6', bgColor: '#EAF1FF' }
  return { textColor: '#69697A', bgColor: '#F2F4F2' }
}

const displayStatus = (s: string) => {
  if (!s) return '—'
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// ────────────────────────── Tab Config ──────────────────────────

interface TabConfig {
  label: string
  key: TabKey
  columns: { header: string; accessor: (row: any) => string }[]
  statusOptions: string[]
  useCalendar: boolean
  exportType: string
}

const tabConfigs: TabConfig[] = [
  {
    label: 'Contracts', key: 'contracts',
    columns: [
      { header: 'Contract ID', accessor: (r) => r.contractNumber || '—' },
      { header: 'Customer', accessor: (r) => r.vendor?.name || '—' },
      { header: 'Product', accessor: (r) => r.sku || '—' },
      { header: 'Start Date', accessor: (r) => formatDate(r.startDate) },
      { header: 'End Date', accessor: (r) => formatDate(r.endDate) },
      { header: 'Contract Value', accessor: (r) => r.price ? formatCurrency(r.price) : '—' },
      { header: 'Type', accessor: (r) => (r.type || '—').charAt(0).toUpperCase() + (r.type || '').slice(1) },
    ],
    statusOptions: ['Status', 'buy', 'sale'],
    useCalendar: false,
    exportType: 'contracts',
  },
  {
    label: 'Shipments', key: 'shipments',
    columns: [
      { header: 'Container No.', accessor: (r) => r.containerNumber || '—' },
      { header: 'Buy Contract', accessor: (r) => r.buyContract || '—' },
      { header: 'Origin', accessor: (r) => r.pol || '—' },
      { header: 'Destination', accessor: (r) => r.pod || '—' },
      { header: 'Customer', accessor: (r) => r.customer || '—' },
      { header: 'ETA', accessor: (r) => formatDate(r.eta) },
      { header: 'Status', accessor: (r) => displayStatus(r.status || '') },
    ],
    statusOptions: ['Status', 'Pending', 'In Transit', 'Completed', 'Delayed'],
    useCalendar: false,
    exportType: 'shipments',
  },
  {
    label: 'Storage & Inventory', key: 'inventory',
    columns: [
      { header: 'Product', accessor: (r) => r.product || '—' },
      { header: 'Warehouse', accessor: (r) => r.warehouse || '—' },
      { header: 'Location', accessor: (r) => r.location || '—' },
      { header: 'Container No.', accessor: (r) => r.containerNumber || '—' },
      { header: 'Pieces', accessor: (r) => r.pieces != null ? String(r.pieces) : '—' },
      { header: 'Weight (kg)', accessor: (r) => r.weight != null ? String(Number(r.weight).toLocaleString()) : '—' },
      { header: 'Days in WH', accessor: (r) => r.daysInWarehouse != null ? String(r.daysInWarehouse) : '—' },
      { header: 'Status', accessor: (r) => r.dispatchDate ? 'Dispatched' : 'In Warehouse' },
    ],
    statusOptions: ['Status', 'In Warehouse', 'Dispatched', 'Damaged'],
    useCalendar: false,
    exportType: 'inventory',
  },
  {
    label: 'Stock', key: 'stock',
    columns: [
      { header: 'Warehouse', accessor: (r) => r.warehouse?.name || '—' },
      { header: 'Customer', accessor: (r) => r.customer || '—' },
      { header: 'Qty', accessor: (r) => r.qtyDispatched != null ? `${Number(r.qtyDispatched)} ${r.qtyUnit || 'MT'}` : '—' },
      { header: 'Destination', accessor: (r) => r.destination || '—' },
      { header: 'Dispatch Date', accessor: (r) => formatDate(r.dispatchDate) },
      { header: 'Product', accessor: (r) => r.product || '—' },
      { header: 'Status', accessor: (r) => displayStatus(r.status || '') },
    ],
    statusOptions: ['Status', 'In Transit', 'In Progress', 'Rerouted', 'Completed'],
    useCalendar: true,
    exportType: 'stock',
  },
  {
    label: 'Payments', key: 'payments',
    columns: [
      { header: 'Invoice No', accessor: (r) => r.invoice || '—' },
      { header: 'Customer', accessor: (r) => r.vendor?.name || r.shipment?.customer || '—' },
      { header: 'Amount', accessor: (r) => formatCurrency(r.amount) },
      { header: 'Paid', accessor: (r) => formatCurrency(r.paidAmount) },
      { header: 'Due', accessor: (r) => formatCurrency(Number(r.amount || 0) - Number(r.paidAmount || 0)) },
      { header: 'Due Date', accessor: (r) => formatDate(r.dueDate) },
      { header: 'Status', accessor: (r) => displayStatus(r.status || '') },
    ],
    statusOptions: ['Status', 'Pending', 'Partial', 'Completed', 'Overdue', 'Paid'],
    useCalendar: true,
    exportType: 'payments',
  },
  {
    label: 'Supplier Reports', key: 'supplier',
    columns: [
      { header: 'Supplier Name', accessor: (r) => r.supplierName || '—' },
      { header: 'Product', accessor: (r) => r.product || '—' },
      { header: 'Total Ordered', accessor: (r) => String(r.total ?? 0) },
      { header: 'Delivered', accessor: (r) => String(r.delivered ?? 0) },
      { header: 'Being Delivered', accessor: (r) => String(r.inTransit ?? 0) },
      { header: 'To Be Delivered', accessor: (r) => String(r.pending ?? 0) },
      { header: 'Status', accessor: (r) => r.status || '—' },
    ],
    statusOptions: ['Status', 'Completed', 'Delayed', 'On Track'],
    useCalendar: true,
    exportType: 'suppliers',
  },
  {
    label: 'Client Reports', key: 'client',
    columns: [
      { header: 'Customer Name', accessor: (r) => r.customerName || '—' },
      { header: 'Product', accessor: (r) => r.product || '—' },
      { header: 'Qty Ordered', accessor: (r) => String(r.total ?? 0) },
      { header: 'Delivered', accessor: (r) => String(r.delivered ?? 0) },
      { header: 'Being Delivered', accessor: (r) => String(r.inTransit ?? 0) },
      { header: 'Pending', accessor: (r) => String(r.pending ?? 0) },
      { header: 'Status', accessor: (r) => r.status || '—' },
    ],
    statusOptions: ['Status', 'Completed', 'Delayed', 'On Track'],
    useCalendar: true,
    exportType: 'clients',
  },
  {
    label: 'Product Reports', key: 'product',
    columns: [
      { header: 'Product Name', accessor: (r) => r.productName || '—' },
      { header: 'Total Ordered', accessor: (r) => String(r.total ?? 0) },
      { header: 'Delivered', accessor: (r) => String(r.delivered ?? 0) },
      { header: 'Being Delivered', accessor: (r) => String(r.inTransit ?? 0) },
      { header: 'Pending', accessor: (r) => String(r.pending ?? 0) },
      { header: 'Status', accessor: (r) => r.status || '—' },
    ],
    statusOptions: ['Status', 'Completed', 'Delayed', 'On Track'],
    useCalendar: true,
    exportType: 'products',
  },
]

// ────────────────────────── Table Title ──────────────────────────

const tableTitles: Record<TabKey, string> = {
  contracts: 'Contract Performance',
  shipments: 'Shipments Status',
  inventory: 'Inventory Status',
  stock: 'Stock Status',
  payments: 'Payments Status',
  supplier: 'Supplier Reports',
  client: 'Client Reports',
  product: 'Product Reports',
}

// ────────────────────────── Status-column indexes ──────────────────────────

// The last "real" column in each tab is the Status column; Action buttons follow it.
function isStatusColumn(header: string) {
  return ['Status', 'Stock Level', 'Type'].includes(header)
}

// ────────────────────────── Component ──────────────────────────

// Map report tab to document API entity type (backend relatedEntityType)
const reportTabToEntityType: Partial<Record<TabKey, string>> = {
  contracts: 'contract',
  shipments: 'shipment',
  inventory: 'inventory',
  stock: 'stock_dispatch',
  payments: 'payment',
}

const Reports = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const getInitialTab = (): TabKey => {
    if (location.pathname === ROUTES.REPORTS_INVENTORY) return 'inventory'
    if (location.pathname === ROUTES.REPORTS_SHIPMENTS) return 'shipments'
    if (location.pathname === ROUTES.REPORTS_PAYMENTS) return 'payments'
    return 'contracts'
  }

  const [activeTab, setActiveTab] = useState<TabKey>(getInitialTab())
  const [statusFilter, setStatusFilter] = useState('Status')
  const [currentPage, setCurrentPage] = useState(1)
  const [resultsPerPage, setResultsPerPage] = useState(10)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null)
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null)
  const [dateLabel, setDateLabel] = useState('Select date')
  const [exporting, setExporting] = useState(false)
  const calendarRef = useRef<HTMLDivElement>(null)

  // Daily AI Summary (test in app)
  const [dailyAILatest, setDailyAILatest] = useState<DailyAISummaryReport | null>(null)
  const [dailyAILoading, setDailyAILoading] = useState(false)
  const [dailyAIRunning, setDailyAIRunning] = useState(false)
  const [dailyAIExpanded, setDailyAIExpanded] = useState<string | null>(null)

  // Row detail popup (view one row + documents)
  const [viewDetailItem, setViewDetailItem] = useState<any | null>(null)
  const [viewDetailDocuments, setViewDetailDocuments] = useState<DocumentListItem[]>([])
  const [viewDetailDocsLoading, setViewDetailDocsLoading] = useState(false)

  const config = tabConfigs.find((t) => t.key === activeTab)!

  // Build API params
  const apiStartDate = selectedStartDate ? formatApiDate(selectedStartDate) : undefined
  const apiEndDate = selectedEndDate ? formatApiDate(selectedEndDate) : undefined

  // For contracts tab, status filter maps to type; for others, it maps to status
  const apiStatus = statusFilter !== 'Status' ? statusFilter : undefined
  const queryParams = activeTab === 'contracts'
    ? { tab: activeTab, page: currentPage, limit: resultsPerPage, startDate: apiStartDate, endDate: apiEndDate, status: undefined }
    : { tab: activeTab, page: currentPage, limit: resultsPerPage, status: apiStatus, startDate: apiStartDate, endDate: apiEndDate }

  // For contracts we need to handle type differently
  const { data, meta, loading, error, errorCode } = useReports(
    activeTab === 'contracts' && statusFilter !== 'Status'
      ? { ...queryParams, status: statusFilter }
      : queryParams
  )

  const totalResults = meta?.total ?? 0
  const totalPages = meta?.totalPages ?? 1

  // ─── Tab switch ───
  const handleTabClick = (tabKey: TabKey) => {
    setActiveTab(tabKey)
    setCurrentPage(1)
    setStatusFilter('Status')
    setIsCalendarOpen(false)
    setSelectedStartDate(null)
    setSelectedEndDate(null)
    const tabCfg = tabConfigs.find((t) => t.key === tabKey)
    setDateLabel(tabCfg?.useCalendar ? 'Pick a date' : 'Select date')
  }

  // ─── Date picker ───
  const handleDateSelect = (start: Date | null, end: Date | null) => {
    setSelectedStartDate(start)
    setSelectedEndDate(end)
    if (start && end) {
      const fmt = (d: Date) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
      }
      setDateLabel(`${fmt(start)} - ${fmt(end)}`)
      setIsCalendarOpen(false)
    }
    setCurrentPage(1)
  }

  // Preset date helper
  const applyPresetDate = (preset: string) => {
    const now = new Date()
    let start: Date | null = null
    let end: Date | null = new Date(now)

    switch (preset) {
      case 'Today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'This Week': {
        const day = now.getDay()
        start = new Date(now)
        start.setDate(now.getDate() - day)
        break
      }
      case 'This Month':
        start = new Date(now.getFullYear(), now.getMonth(), 1)
        break
      case 'This Year':
        start = new Date(now.getFullYear(), 0, 1)
        break
      default:
        start = null
        end = null
    }

    setSelectedStartDate(start)
    setSelectedEndDate(end)
    if (start && end) {
      const fmt = (d: Date) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
      }
      setDateLabel(`${fmt(start)} - ${fmt(end)}`)
    } else {
      setDateLabel('Select date')
    }
    setCurrentPage(1)
  }

  // ─── View detail (popup with row data + documents/PDF) ───
  const tabsWithDetailView: TabKey[] = ['contracts', 'shipments', 'inventory', 'stock', 'payments']
  const handleViewDetail = useCallback(
    async (item: any) => {
      if (!item) return
      setViewDetailItem(item)
      setViewDetailDocuments([])
      const entityType = reportTabToEntityType[activeTab]
      const entityId = item?.id
      if (entityType && entityId) {
        setViewDetailDocsLoading(true)
        try {
          const docs = await getDocumentsByEntity(entityType, entityId)
          setViewDetailDocuments(docs)
        } catch {
          setViewDetailDocuments([])
        } finally {
          setViewDetailDocsLoading(false)
        }
      }
    },
    [activeTab]
  )

  const handleCloseViewDetail = useCallback(() => {
    setViewDetailItem(null)
    setViewDetailDocuments([])
  }, [])

  const handleOpenDocument = useCallback(async (doc: DocumentListItem) => {
    try {
      const blob = await apiService.instance.get(`/documents/${doc.id}/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(blob.data as Blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch {
      alert('Could not open document. You may need to download it from the Documents page.')
    }
  }, [])

  // ─── Export ───
  const handleExport = useCallback(async () => {
    try {
      setExporting(true)
      const blob = await exportReport({
        type: config.exportType,
        format: 'xlsx',
        startDate: apiStartDate,
        endDate: apiEndDate,
        status: apiStatus,
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${config.label}_Report.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      alert('Export failed. You may not have permission to export reports.')
    } finally {
      setExporting(false)
    }
  }, [config, apiStartDate, apiEndDate, apiStatus])

  // ─── Close calendar on outside click ───
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false)
      }
    }
    if (isCalendarOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCalendarOpen])

  // ─── Daily AI Summary: fetch latest on mount ───
  const refreshDailyAI = useCallback(async () => {
    setDailyAILoading(true)
    try {
      const report = await getDailyAISummaryLatest()
      setDailyAILatest(report ?? null)
    } catch {
      setDailyAILatest(null)
    } finally {
      setDailyAILoading(false)
    }
  }, [])
  useEffect(() => {
    refreshDailyAI()
  }, [refreshDailyAI])

  const handleRunDailyAI = useCallback(async () => {
    setDailyAIRunning(true)
    try {
      const report = await runDailyAISummaryNow()
      setDailyAILatest(report)
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to run daily AI summary. Sign in as admin or operations manager.')
    } finally {
      setDailyAIRunning(false)
    }
  }, [])

  // ────────────────────────── Render ──────────────────────────

  const headerStyle = {
    fontFamily: 'Lexend Deca',
    fontWeight: 400 as const,
    fontStyle: 'normal' as const,
    fontSize: '12px',
    lineHeight: '20px',
    letterSpacing: '0%',
    color: '#69697A',
    borderLeft: 'none',
    borderRight: 'none',
    paddingLeft: '16px',
  }

  const cellStyle = {
    fontFamily: 'Lexend Deca',
    fontWeight: 400 as const,
    fontStyle: 'normal' as const,
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0%',
    color: '#212126',
    paddingLeft: '16px',
  }

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      {/* Header Section */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
          <h1 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: 'clamp(18px, 4vw, 20px)', lineHeight: '28px', color: '#212126' }}>
            Reports
          </h1>
          <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: 'clamp(11px, 2.5vw, 12px)', lineHeight: '28px', display: 'flex', alignItems: 'center' }}>
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Reports</span>
          </span>
        </div>

        {/* Daily AI Summary — run and view latest (requires login) */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4" style={{ border: '1px solid #E9E9EA', borderRadius: '8px' }}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '16px', color: '#212126' }}>Daily AI Summary</h3>
              <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', color: '#69697A', marginTop: '4px' }}>
                One report per module per day: analysis, recommendations, and alerts (contracts, shipments, clearing, inventory, finance, reporting). No manual &quot;ask AI&quot;—scheduled or run now.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={refreshDailyAI}
                disabled={dailyAILoading}
                className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                {dailyAILoading ? 'Loading...' : 'Refresh'}
              </button>
              <button
                type="button"
                onClick={handleRunDailyAI}
                disabled={dailyAIRunning}
                className="px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: '#212126' }}
              >
                {dailyAIRunning ? 'Running...' : 'Run now'}
              </button>
            </div>
          </div>
          {dailyAILatest?.payload && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>
                Report date: {dailyAILatest.payload.reportDate} · Generated: {new Date(dailyAILatest.payload.generatedAt).toLocaleString()}
              </p>
              <div className="mt-3 grid gap-2">
                {(['contracts', 'shipments', 'clearing', 'inventory', 'finance', 'reporting'] as const).map((mod) => {
                  const moduleData = dailyAILatest.payload.modules[mod]
                  const isExpanded = dailyAIExpanded === mod
                  const isLegacy = typeof moduleData === 'string'
                  const analysis = isLegacy ? (moduleData as string) : (moduleData as { analysis?: string; recommendations?: string[]; alerts?: string[] }).analysis
                  const recommendations = !isLegacy && Array.isArray((moduleData as { recommendations?: string[] }).recommendations) ? (moduleData as { recommendations: string[] }).recommendations : []
                  const alerts = !isLegacy && Array.isArray((moduleData as { alerts?: string[] }).alerts) ? (moduleData as { alerts: string[] }).alerts : []
                  const alertCount = alerts.length
                  return (
                    <div key={mod} className="border border-gray-100 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setDailyAIExpanded(isExpanded ? null : mod)}
                        className="w-full px-3 py-2 flex items-center justify-between text-left bg-gray-50 hover:bg-gray-100"
                        style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '14px', color: '#212126' }}
                      >
                        <span style={{ textTransform: 'capitalize' }}>{mod}</span>
                        <span className="flex items-center gap-2">
                          {alertCount > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">{alertCount} alert{alertCount !== 1 ? 's' : ''}</span>
                          )}
                          <FiChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="p-3 bg-white text-sm" style={{ fontFamily: 'Lexend Deca', color: '#212126', maxHeight: '360px', overflowY: 'auto' }}>
                          <p className="whitespace-pre-wrap mb-3">{analysis || 'No analysis.'}</p>
                          {recommendations.length > 0 && (
                            <div className="mb-3">
                              <p className="font-semibold text-gray-700 mb-1">Recommendations</p>
                              <ul className="list-disc pl-4 space-y-0.5">
                                {recommendations.map((r, i) => (
                                  <li key={i}>{r}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {alerts.length > 0 && (
                            <div>
                              <p className="font-semibold text-amber-700 mb-1">Alerts</p>
                              <ul className="list-disc pl-4 space-y-0.5 text-amber-800">
                                {alerts.map((a, i) => (
                                  <li key={i}>{a}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          {!dailyAILoading && !dailyAILatest && (
            <p className="mt-3 text-sm" style={{ color: '#69697A' }}>No daily AI summary yet. Sign in and click &quot;Run now&quot; to generate.</p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between ">
          <div
            className="flex shrink-1 items-center overflow-x-auto scrollbar-hide "
            style={{ display: 'flex', flexGrow: 1, width: '100%', maxWidth: '1200px', height: '38px', borderRadius: '8px', border: '1px solid #E9E9EA', backgroundColor: '#FFFFFF', padding: '4px', gap: '4px' }}
          >
            {tabConfigs.map((tab) => {
              const isActive = activeTab === tab.key
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabClick(tab.key)}
                  style={{
                    fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px',
                    color: isActive ? '#FFFFFF' : '#69697A',
                    backgroundColor: isActive ? '#212126' : 'transparent',
                    padding: '6px 20px', borderRadius: '6px', height: '30px',
                    display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center',
                    whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* When Payments tab is active, show full Finance Purchases page; otherwise show report table */}
      {activeTab === 'payments' ? (
        <FinancePurchases embeddedInReports />
      ) : (
      <div className="bg-white rounded-lg border border-gray-200" style={{ paddingBottom: '24px', border: '1px solid #E9E9EA', borderRadius: '8px' }}>
        {/* Title + Filters */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h2 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: 'clamp(16px, 3.5vw, 18px)', lineHeight: '26px', color: '#212126' }}>
            {tableTitles[activeTab]}
          </h2>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4" style={{ minHeight: '40px' }}>
            {/* Status Dropdown */}
            <div className="relative w-full sm:w-auto">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1) }}
                className="px-3 sm:px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none text-sm sm:text-base w-full sm:w-auto"
                style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: 'clamp(12px, 3vw, 14px)', lineHeight: '20px', color: '#050521', height: '40px', minWidth: '120px' }}
              >
                {config.statusOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt === 'Status' ? 'Status' : displayStatus(opt)}</option>
                ))}
              </select>
              <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#050521' }} />
            </div>

            {/* Date Filter */}
            <div className="relative w-full sm:w-auto" ref={calendarRef}>
              {config.useCalendar ? (
                <>
                  <img src="/images/calendar.svg" alt="Calendar" className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10" style={{ width: '16px', height: '16px', opacity: 0.8, filter: 'brightness(0) saturate(100%) invert(8%) sepia(4%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)' }} />
                  <button
                    onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                    className="px-3 sm:px-4 py-2 pl-11 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-full text-left text-sm sm:text-base"
                    style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: 'clamp(12px, 3vw, 14px)', lineHeight: '20px', color: '#050521', height: '40px', minWidth: '150px', backgroundColor: '#FFFFFF', paddingLeft: '44px' }}
                  >
                    {dateLabel}
                  </button>
                  <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#050521' }} />
                  <RangeCalendar
                    isOpen={isCalendarOpen}
                    onClose={() => setIsCalendarOpen(false)}
                    onDateSelect={handleDateSelect}
                    selectedStartDate={selectedStartDate}
                    selectedEndDate={selectedEndDate}
                  />
                </>
              ) : (
                <>
                  <img src="/images/calendar.svg" alt="Calendar" className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10" style={{ width: '16px', height: '16px', opacity: 0.8 }} />
                  <select
                    value={dateLabel}
                    onChange={(e) => {
                      setDateLabel(e.target.value)
                      if (e.target.value === 'Custom Range') {
                        setIsCalendarOpen(true)
                      } else if (e.target.value === 'Select date') {
                        setSelectedStartDate(null)
                        setSelectedEndDate(null)
                        setCurrentPage(1)
                      } else {
                        applyPresetDate(e.target.value)
                      }
                    }}
                    className="px-3 sm:px-4 py-2 pl-11 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none text-sm sm:text-base w-full sm:w-auto"
                    style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: 'clamp(12px, 3vw, 14px)', lineHeight: '20px', color: '#050521', height: '40px', minWidth: '150px', paddingLeft: '44px' }}
                  >
                    <option>Select date</option>
                    <option>Today</option>
                    <option>This Week</option>
                    <option>This Month</option>
                    <option>This Year</option>
                    <option>Custom Range</option>
                  </select>
                  <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#050521' }} />
                  {/* Inline calendar for custom range */}
                  {isCalendarOpen && (
                    <RangeCalendar
                      isOpen={isCalendarOpen}
                      onClose={() => setIsCalendarOpen(false)}
                      onDateSelect={handleDateSelect}
                      selectedStartDate={selectedStartDate}
                      selectedEndDate={selectedEndDate}
                    />
                  )}
                </>
              )}
            </div>

            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={exporting || loading}
              className="flex items-center justify-center hover:opacity-90 transition-opacity w-full sm:w-auto text-sm sm:text-base disabled:opacity-50"
              style={{
                minWidth: '65px', height: '34px', borderRadius: '6px',
                paddingTop: '8px', paddingRight: '12px', paddingBottom: '8px', paddingLeft: '12px',
                backgroundColor: '#212126', fontFamily: 'Lexend Deca', fontWeight: 600,
                fontSize: 'clamp(11px, 2.5vw, 12px)', lineHeight: '20px', color: '#FFFFFF',
              }}
            >
              {exporting ? (
                <span className="flex items-center gap-1"><FiLoader className="animate-spin w-3 h-3" /> Exporting...</span>
              ) : (
                <span>Export</span>
              )}
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="px-4 sm:px-6" style={{ marginBottom: '32px' }}>
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <FiLoader className="animate-spin" style={{ width: '32px', height: '32px', color: '#212126' }} />
                <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#69697A' }}>Loading report data...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3 text-center">
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  backgroundColor: errorCode === 'unauthorized' ? '#FEF9EB' : '#FDEBE9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <span style={{ fontSize: '20px', color: errorCode === 'unauthorized' ? '#EEC233' : '#E93323' }}>
                    {errorCode === 'unauthorized' ? '🔒' : errorCode === 'forbidden' ? '🚫' : '!'}
                  </span>
                </div>
                <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '16px', color: '#212126' }}>
                  {errorCode === 'unauthorized' ? 'Login Required' : errorCode === 'forbidden' ? 'Access Denied' : 'Failed to load report'}
                </span>
                <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#69697A', maxWidth: '400px' }}>{error}</span>
                {errorCode === 'unauthorized' && (
                  <button
                    onClick={() => navigate(ROUTES.LOGIN)}
                    className="mt-2 hover:opacity-90 transition-opacity"
                    style={{
                      fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '14px',
                      backgroundColor: '#212126', color: '#FFFFFF',
                      padding: '10px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    }}
                  >
                    Go to Login
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && data.length === 0 && (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3 text-center">
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#F2F4F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '20px', color: '#69697A' }}>0</span>
                </div>
                <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '16px', color: '#212126' }}>No data found</span>
                <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#69697A' }}>Try adjusting your filters or date range.</span>
              </div>
            </div>
          )}

          {/* Table */}
          {!loading && !error && data.length > 0 && (
            <div className="w-full overflow-x-auto sm:overflow-visible" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #E9E9EA', width: '100%', minWidth: `${Math.max(750, config.columns.length * 130)}px` }}>
                <table key={activeTab} className="w-full" style={{ borderCollapse: 'collapse', minWidth: `${Math.max(750, config.columns.length * 130)}px` }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E9E9EA' }}>
                      {config.columns.map((col, i) => (
                        <th
                          key={col.header}
                          className="text-left py-2"
                          style={{
                            ...headerStyle,
                            ...(i === 0 ? { borderTopLeftRadius: '8px' } : {}),
                          }}
                        >
                          {col.header}
                        </th>
                      ))}
                      <th className="text-left py-2" style={{ ...headerStyle, borderTopRightRadius: '8px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item: any, index: number) => {
                      const isLastRow = index === data.length - 1
                      return (
                        <tr
                          key={item.id || index}
                          className="hover:bg-gray-50 transition-colors"
                          style={{ borderBottom: isLastRow ? 'none' : '1px solid #E9E9EA' }}
                        >
                          {config.columns.map((col) => {
                            const value = col.accessor(item)
                            const isStatus = isStatusColumn(col.header)

                            if (isStatus) {
                              const colors = getStatusColor(value)
                              return (
                                <td key={col.header} className="py-3" style={{ paddingLeft: '16px' }}>
                                  <span
                                    className="inline-block text-xs font-medium"
                                    style={{
                                      fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px',
                                      lineHeight: '20px', color: colors.textColor, backgroundColor: colors.bgColor,
                                      borderRadius: '6px', padding: '4px 12px', display: 'inline-block',
                                    }}
                                  >
                                    {value}
                                  </span>
                                </td>
                              )
                            }

                            return (
                              <td key={col.header} className="py-3" style={cellStyle}>
                                {value}
                              </td>
                            )
                          })}
                          <td className="py-3" style={{ paddingLeft: '16px' }}>
                            <div className="flex items-center gap-3">
                              {tabsWithDetailView.includes(activeTab) && item?.id && (
                                <button
                                  type="button"
                                  onClick={() => handleViewDetail(item)}
                                  className="p-2 hover:opacity-80 rounded transition-colors"
                                  title="View details"
                                  style={{ backgroundColor: '#E0E7FF', padding: '8px' }}
                                >
                                  <img src="/images/eye.svg" alt="View" className="w-4 h-4" />
                                </button>
                              )}
                              {activeTab !== 'contracts' && activeTab !== 'shipments' && activeTab !== 'stock' && (
                                <button
                                  onClick={handleExport}
                                  className="p-2 hover:opacity-80 rounded transition-colors"
                                  title="Download report"
                                  style={{ backgroundColor: '#E8F7EF', padding: '8px' }}
                                >
                                  <img src="/images/download.svg" alt="Download" className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '1px solid #E9E9EA' }}>
                      <td colSpan={config.columns.length + 1} className="px-6" style={{ paddingTop: '16px', paddingBottom: '20px' }}>
                        <div className="flex items-center justify-between">
                          <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                            Showing {Math.min(currentPage * resultsPerPage, totalResults)} of {totalResults} Results
                          </span>
                          <div className="flex items-center gap-2">
                          <PageSizeSelect
                            value={resultsPerPage}
                            onChange={(size) => {
                              setResultsPerPage(size)
                              setCurrentPage(1)
                            }}
                          />
                            <button
                              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#212126' }}
                            >
                              <FiChevronLeft className="w-4 h-4" />
                              <span>Previous</span>
                            </button>
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                              let pageNum: number
                              if (totalPages <= 5) {
                                pageNum = i + 1
                              } else if (currentPage <= 3) {
                                pageNum = i + 1
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i
                              } else {
                                pageNum = currentPage - 2 + i
                              }
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => setCurrentPage(pageNum)}
                                  className="px-3 py-2 border rounded-lg transition-colors"
                                  style={{
                                    fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px',
                                    color: currentPage === pageNum ? '#FFFFFF' : '#212126',
                                    backgroundColor: currentPage === pageNum ? '#212126' : 'transparent',
                                    borderColor: currentPage === pageNum ? '#212126' : '#E9E9EA',
                                  }}
                                >
                                  {pageNum}
                                </button>
                              )
                            })}
                            <button
                              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                              disabled={currentPage === totalPages}
                              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#212126' }}
                            >
                              <span>Next</span>
                              <FiChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Row detail popup: single row data + documents/PDF */}
      {viewDetailItem && config && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleCloseViewDetail}
        >
          <div
            className="bg-white rounded-xl shadow-lg max-h-[90vh] w-full max-w-lg overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '18px', color: '#212126' }}>
                {tableTitles[activeTab]} — Details
              </h3>
              <button
                type="button"
                onClick={handleCloseViewDetail}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <FiX className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              <div className="space-y-2">
                {config.columns.map((col) => (
                  <div key={col.header} className="flex justify-between gap-4 py-1.5 border-b border-gray-100 last:border-0">
                    <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '13px', color: '#69697A' }}>
                      {col.header}
                    </span>
                    <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#212126', textAlign: 'right' }}>
                      {col.accessor(viewDetailItem)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="pt-3 border-t border-gray-200">
                <h4 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '14px', color: '#212126', marginBottom: '8px' }}>
                  Documents / PDF
                </h4>
                {viewDetailDocsLoading ? (
                  <div className="flex items-center gap-2 text-gray-500" style={{ fontFamily: 'Lexend Deca', fontSize: '14px' }}>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Loading documents…
                  </div>
                ) : viewDetailDocuments.length === 0 ? (
                  <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#69697A' }}>
                    No documents or PDFs linked to this record.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {viewDetailDocuments.map((doc) => (
                      <li key={doc.id} className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-gray-50">
                        <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', color: '#212126', overflow: 'hidden', textOverflow: 'ellipsis' }} title={doc.name}>
                          {doc.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleOpenDocument(doc)}
                          className="shrink-0 px-3 py-1.5 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: '#212126', fontFamily: 'Lexend Deca' }}
                        >
                          View
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Reports
